import React, {useMemo, useState} from "react";
import useIsMounted from "../../../hooks/use-is-mounted";
import classnames from "classnames";
import IconButton from "../../base/button/icon_button";
import {ArrowBack, CloudSync} from "@mui/icons-material";
import Button from "../../base/button/button";
import AddressCardTaxInfoSection from "./tax-info";
import AddressCardActionsPopover, {AddressCardActions} from "./actions-popover";
import {CircularProgress} from "@mui/material";

export {AddressCardActions};

const AddressCard = ({
                         address,
                         remove,
                         edit,
                         editTaxes,
                         setDefault,
                         duplicate,
                         onClick,
                         syncProvinceTaxes: syncProvinceTaxesProps,
                         disabled: disabledProps = false,
                         readonly = false,
                         excludeActions = [],
                     }) => {
    const [showTaxInfo, setShowTaxInfo] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [syncingProvinceTaxes, setSyncingProvinceTaxes] = useState(false);
    const isMounted = useIsMounted();

    const hideTaxInfo = useMemo(() =>
            excludeActions?.includes(AddressCardActions.taxInfo) || readonly,
        [excludeActions, readonly])

    /**@type {boolean}*/
    const disabled = useMemo(() => removing || disabledProps, [removing, disabledProps])

    const actions = useMemo(() => {
        const actions = Object.values(AddressCardActions);
        const excluded = Array.from(excludeActions ?? []);
        if (address?.isDefault) {
            excluded.push(AddressCardActions.setDefault);
            excluded.push(AddressCardActions.remove);
        }
        return actions.filter(e => !excluded.includes(e));
    }, [excludeActions, address?.isDefault])

    /**@type {string}*/
    const addressText = useMemo(() => {
        let res = '';
        if (!address)
            return res;
        if (address.aptNo?.length) {
            res = res.concat(address.aptNo)
            res = res.concat('-')
        }
        if (address.streetAddress?.length) {
            res = res.concat(address.streetAddress)
        }
        res = res.concat('<br/>');
        if (address.city?.length) {
            res = res.concat(address.city)
            res = res.concat(', ')
        }
        if (address.province?.name?.length) {
            res = res.concat(address.province?.name)
            res = res.concat(', ')
        }
        res = res.concat('<br/>');
        if (address?.postalCode?.length) {
            res = res.concat(address.postalCode.toUpperCase())
            res = res.concat(', ')
        }
        if (address.country?.name?.length ?? address?.province?.country?.name?.length) {
            res = res.concat(address.country?.name ?? address?.province?.country?.name)
        }
        return res;
    }, [address])

    /**
     * Invokes the remove [callback] from the props.
     *
     * * sets the removing state value so that the ui can disable the applicable actions of this component.
     * @return {Promise<void>}
     */
    const removeAddress = async () => {
        setRemoving(true);
        await remove();
        if (!isMounted())
            return;
        setRemoving(false);
    }

    /**
     * Invokes the [onClick] callback of the props.
     *
     * * if [disabled] or [showTaxInfo] is set, it does nothing
     * * if [onClick] callback is not defined, does nothing.
     * @param {MouseEvent} e
     */
    const onAddressClicked = (e) => {
        if (disabled || showTaxInfo || !onClick)
            return;
        onClick(e);
    }

    /**
     * Invokes the [syncProvinceTaxes] callback of the props.
     * @param address
     * @returns {Promise<void>}
     */
    const syncProvinceTaxes = async (address) => {
        setSyncingProvinceTaxes(true);
        await syncProvinceTaxesProps(address);
        if (!isMounted())
            return;
        setSyncingProvinceTaxes(false);
    }

    return (
        <>
            <div
                onClick={onAddressClicked}
                className={classnames('address-card', {'clickable': !disabled && !showTaxInfo && !!onClick})}
            >
                <div className={'address-card-header'}>
                    {
                        showTaxInfo &&
                        <div className={'tax-info-title-section'}>
                            <IconButton onPressed={() => setShowTaxInfo(false)}>
                                <ArrowBack className='icon normal'/>
                            </IconButton>
                            <p className={'title'}>
                                Tax Information
                            </p>
                        </div>
                    }
                    {
                        !showTaxInfo &&
                        <div className={'address-title-section'}>
                            <p className={'title'}>
                                {address?.title ?? ''}
                            </p>
                            <p className={'type'}>
                                {address?.type?.title ?? ''}
                            </p>
                        </div>
                    }
                    <div className={'d-flex align-items-center justify-content-end'}>
                        {
                            !readonly && !hideTaxInfo && showTaxInfo && syncProvinceTaxesProps &&
                            <IconButton
                                onPressed={() => syncProvinceTaxes(address)}
                                disabled={disabled}
                                color={'paper.secondary'}
                            >
                                {
                                    syncingProvinceTaxes
                                        ? <CircularProgress color={'secondary'} size={20}/>
                                        : <CloudSync/>
                                }
                            </IconButton>
                        }
                        {
                            !readonly && !!actions.length &&
                            <AddressCardActionsPopover
                                actions={actions}
                                disabled={disabled}
                                edit={edit}
                                duplicate={duplicate}
                                setDefault={setDefault}
                                {...!!remove ? {remove: removeAddress} : {}}
                                addressTypeId={address?.type?.id}
                            />
                        }
                    </div>
                </div>
                {
                    showTaxInfo &&
                    <AddressCardTaxInfoSection
                        addressTaxes={address?.addressTaxes}
                        setShowTaxInfo={setShowTaxInfo}
                        edit={editTaxes}
                    />
                }
                {
                    !showTaxInfo &&
                    <>
                        <div className={'address-card-content'}>
                            <p dangerouslySetInnerHTML={{__html: addressText}}/>
                        </div>
                        <div className={'address-card-footer'}>
                            <div>
                                {
                                    address?.isDefault &&
                                    <div className={'default-indicator'}>
                                        Default Address
                                    </div>
                                }
                            </div>
                            {
                                !hideTaxInfo &&
                                <div>
                                    <Button
                                        style={{margin: '2px 0'}}
                                        disabled={disabled}
                                        variant={'text'}
                                        color={'primary'}
                                        onClick={() => setShowTaxInfo(true)}
                                    >
                                        Tax Info
                                    </Button>
                                </div>
                            }
                        </div>
                    </>
                }
            </div>
        </>
    );
}

export default AddressCard;
